import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  FiUser,
  FiMail,
  FiFileText,
  FiSend,
  FiAlertTriangle,
  FiMessageSquare
} from "react-icons/fi";
import "../styles/SolicitorHelp.css";

export default function SolicitorHelp() {
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  /* ======================
     LOAD AUTH USER DETAILS
  ====================== */
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = await res.json();

        if (data?.user) {
          setFormData(prev => ({
            ...prev,
            name: data.user.name || "",
            email: data.user.email || ""
          }));
        }
      } catch (err) {
        toast.error("Failed to load user info");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [token]);

  /* ======================
     FORM HANDLERS
  ====================== */
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting) return;

    if (!formData.subject || !formData.message) {
      Swal.fire({
        title: 'Required Fields',
        text: 'Please fill in all required fields',
        icon: 'warning',
        confirmButtonColor: '#bfa56a',
        background: '#ffffff',
        color: '#3a2f2c'
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Send Message?',
      text: 'Your message will be sent to admin support.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#bfa56a',
      cancelButtonColor: '#d1d5db',
      confirmButtonText: 'Yes, send it',
      cancelButtonText: 'Cancel',
      background: '#ffffff',
      color: '#3a2f2c'
    });

    if (!result.isConfirmed) return;

    try {
      setSubmitting(true);

      const res = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.message || "Failed to send");

      Swal.fire({
        title: 'Message Sent!',
        text: 'Your message has been sent to Admin Support.',
        icon: 'success',
        confirmButtonColor: '#48bb78',
        background: '#ffffff',
        color: '#3a2f2c',
        timer: 3000,
        showConfirmButton: false
      });

      // Reset only subject & message
      setFormData(prev => ({
        ...prev,
        subject: "",
        message: ""
      }));

    } catch (err) {
      Swal.fire({
        title: 'Error',
        text: err.message || 'Something went wrong',
        icon: 'error',
        confirmButtonColor: '#f56565',
        background: '#ffffff',
        color: '#3a2f2c'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="solicitor-help-loading">
        <div className="solicitor-spinner"></div>
        <p>Loading help center...</p>
      </div>
    );
  }

  return (
    <div className="solicitor-help-wrapper">
      {/* HEADER */}
      <div className="solicitor-help-header">
        <div className="solicitor-header-content">
          <h1 className="solicitor-main-title">Solicitor Help & Support</h1>
          <p className="solicitor-subtitle">Contact admin for assistance or technical support</p>
        </div>
      </div>

      {/* FORM CARD */}
      <div className="solicitor-help-card">
        <form onSubmit={handleSubmit} className="solicitor-help-form">
          {/* NAME */}
          <div className="solicitor-form-group">
            <label className="solicitor-form-label">
              <FiUser className="solicitor-form-icon" />
              Full Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              disabled
              className="solicitor-input solicitor-input-disabled"
            />
          </div>

          {/* EMAIL */}
          <div className="solicitor-form-group">
            <label className="solicitor-form-label">
              <FiMail className="solicitor-form-icon" />
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              disabled
              className="solicitor-input solicitor-input-disabled"
            />
          </div>

          {/* SUBJECT */}
          <div className="solicitor-form-group">
            <label className="solicitor-form-label">
              <FiFileText className="solicitor-form-icon" />
              Subject <span className="solicitor-required">*</span>
            </label>
            <input
              type="text"
              name="subject"
              placeholder="Enter subject"
              value={formData.subject}
              onChange={handleChange}
              required
              className="solicitor-input"
            />
          </div>

          {/* MESSAGE */}
          <div className="solicitor-form-group">
            <label className="solicitor-form-label">
              <FiMessageSquare className="solicitor-form-icon" />
              Message <span className="solicitor-required">*</span>
            </label>
            <textarea
              name="message"
              rows="6"
              placeholder="Describe your issue or request..."
              value={formData.message}
              onChange={handleChange}
              required
              className="solicitor-textarea"
            />
          </div>

          {/* NOTE */}
          <div className="solicitor-help-note">
            <FiAlertTriangle />
            <span>Your message will be sent directly to admin support team.</span>
          </div>

          {/* SUBMIT BUTTON */}
          <button
            type="submit"
            className="solicitor-save-button"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <div className="solicitor-button-spinner"></div>
                Sending...
              </>
            ) : (
              <>
                <FiSend className="solicitor-button-icon" />
                Send to Admin
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}